import type { GraphQLFormattedError } from 'graphql';

/**
 * A GraphQL error from a server response, as a real `Error` so Sentry reports the server's
 * message instead of "Object captured as exception".
 */
export class GraphQLResponseError extends Error {
  readonly code: string | null;
  readonly path: readonly (string | number)[];

  constructor(gqlError: GraphQLFormattedError) {
    super(gqlError.message);
    this.name = 'GraphQLResponseError';
    const code = gqlError.extensions?.code;
    this.code = typeof code === 'string' ? code : null;
    this.path = gqlError.path ?? [];
  }
}

/** The failing field, such as `workflow.tasks.assignee`, with list indices dropped. */
export function getGraphQLErrorField(error: GraphQLResponseError): string | null {
  const fields = error.path.filter(segment => typeof segment === 'string');
  return fields.length ? fields.join('.') : null;
}

/**
 * Groups reports by the field that failed and the error code, so each failing query gets its own
 * Sentry issue. List indices are dropped so one bad row does not split an issue per index. A
 * pathless error (a validation failure) groups by its message.
 */
export function getGraphQLErrorFingerprint(error: GraphQLResponseError): string[] {
  return [
    'graphql-response-error',
    getGraphQLErrorField(error) ?? error.message,
    error.code ?? 'NO_CODE',
  ];
}
